import { Injectable, OnDestroy } from '@angular/core';
import { Client, IMessage } from '@stomp/stompjs';
import * as SockJS from 'sockjs-client';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { AuthService } from './auth.service';
import { AppConfigService} from './app-config.service';

export interface NotificationMessage {
  id: string;
  entityId: string;
  entityType: string;
  eoId: string;
  title: string;
  message: string;
  userId: string;
  createdAt: string;
  read: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationWebSocketService implements OnDestroy {
  private stompClient: Client | null = null;
  private isConnectedSubject$ = new BehaviorSubject<boolean>(false);
  private notificationSubject$ = new Subject<NotificationMessage>();
  private approvalCountSignalSubject$ = new Subject<'INCREMENT' | 'DECREMENT'>();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;
  private reconnectInterval = 5000;
  private isConnecting = false;
  private lastUserId: string | null = null;
  private lastUserRole: string | null = null;

  // Public observables
  public isConnected$ = this.isConnectedSubject$.asObservable();
  public notifications$ = this.notificationSubject$.asObservable();
  public approvalCountSignals$ = this.approvalCountSignalSubject$.asObservable();

  constructor(private authService: AuthService,
    private appConfigService: AppConfigService
  ) {}

  ngOnDestroy(): void {
    this.disconnect();
  }

  /**
   * Connect to notification WebSocket
   */
  connect(): void {
    const userId = this.authService.getUserId();
    const userRole = this.authService.getStoredRole();

    if (!userId) {
      console.warn('[NotificationWS] Cannot connect: User ID not available yet');
      return;
    }

    // Check if we need to reconnect due to user/role change
    if (this.stompClient && (this.lastUserId !== userId || this.lastUserRole !== userRole)) {
      this.disconnect();
    }

    if ((this.stompClient && this.stompClient.connected) || this.isConnecting) {
      return;
    }

    this.lastUserId = userId;
    this.lastUserRole = userRole;
    this.isConnecting = true;
    const wsUrl = this.getWebSocketUrl();

    this.stompClient = new Client({
      webSocketFactory: () => new SockJS(wsUrl),
      
      connectHeaders: {
        // Add auth headers if needed
      },

      reconnectDelay: this.reconnectInterval,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,

      onConnect: (frame) => {
        this.isConnectedSubject$.next(true);
        this.reconnectAttempts = 0;
        this.isConnecting = false;

        // Subscribe to user-specific notifications
        this.stompClient?.subscribe(
          `/topic/notifications/${userId}`,
          (message: IMessage) => this.handleNotification(message),
          { id: `notification-sub-${userId}` }
        );

        // Subscribe to approval count signals for SuperAdmins and HelpDesk
        if (this.authService.isSuperAdmin() || this.authService.isHelpDesk()) {
          this.stompClient?.subscribe(
            '/topic/approval/count',
            (message: IMessage) => this.handleApprovalCountSignal(message),
            { id: 'approval-count-sub' }
          );
        }
      },

      onStompError: (frame) => {
        console.error('[NotificationWS] STOMP Error:', frame);
        this.isConnectedSubject$.next(false);
        this.isConnecting = false;
        this.handleReconnect();
      },

      onWebSocketClose: () => {
        this.isConnectedSubject$.next(false);
        this.isConnecting = false;
        this.handleReconnect();
      }
    });

    this.stompClient.activate();
  }

  /**
   * Get WebSocket URL based on environment/config
   */
  private getWebSocketUrl(): string {
    const baseUrl = AppConfigService.notificationManagerUrl;
    return `${baseUrl}/ws`;
  }

  /**
   * Handle reconnection with backoff
   */
  private handleReconnect(): void {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      setTimeout(() => {
        this.connect();
      }, this.reconnectInterval);
    } else {
      console.warn('[NotificationWS] Max reconnect attempts reached');
    }
  }

  /**
   * Handle incoming notification message
   */
  private handleNotification(message: IMessage): void {
    try {
      const notification: NotificationMessage = JSON.parse(message.body);
      this.notificationSubject$.next(notification);
    } catch (error) {
      console.error('Error parsing notification:', error, message.body);
    }
  }

  /**
   * Handle incoming approval count signal
   */
  private handleApprovalCountSignal(message: IMessage): void {
    try {
      // Try parsing as JSON first
      const payload = JSON.parse(message.body);
      
      // Handle both { "action": "INCREMENT" } and just "INCREMENT" (if Jackson quoted it)
      const action = (typeof payload === 'object' && payload !== null) ? payload.action : payload;
      
      if (action === 'INCREMENT' || action === 'DECREMENT') {
        this.approvalCountSignalSubject$.next(action as 'INCREMENT' | 'DECREMENT');
      } else {
        console.warn('[NotificationWS] Unknown signal action:', action);
      }
    } catch (error) {
      // Fallback for raw non-JSON string
      const signal = message.body;
      if (signal === 'INCREMENT' || signal === 'DECREMENT') {
        this.approvalCountSignalSubject$.next(signal as 'INCREMENT' | 'DECREMENT');
      } else {
        console.error('[NotificationWS] Error parsing signal:', error, message.body);
      }
    }
  }

  /**
   * Disconnect from WebSocket
   */
  disconnect(): void {
    if (this.stompClient) {
      this.stompClient.deactivate();
      this.isConnectedSubject$.next(false);
      this.stompClient = null;
    }
  }

  /**
   * Check connection status
   */
  isConnected(): boolean {
    return this.stompClient?.connected || false;
  }

  /**
   * Force reconnect
   */
  forceReconnect(): void {
    this.disconnect();
    this.reconnectAttempts = 0;
    this.connect();
  }
}
